import { CustomFieldProps, useField } from 'informed';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomFormFieldProps } from './contracts';
import { ProfileSelectBlock } from './primitives/profileSelects/ProfileSelectBlock';
import { SelectBlock } from './primitives/SelectBlock';
import { SelectBlockProps } from './primitives/types';
import { getErrorTranslation } from './utils';
import { ValidatorMessage } from '@/helpers/validators';

function asSelectField<P extends SelectBlockProps, V>(
  Component: React.ComponentType<P>
): React.ComponentType<CustomFieldProps<CustomFormFieldProps, V> & P> {
  return ({
    translateError = true,
    onSelectOption,
    onClose,
    error: propsError,
    ...fieldProps
  }) => {
    const { t } = useTranslation();

    const { fieldState, fieldApi, render, ref, userProps } = useField<P, V>(
      fieldProps
    );
    const { setValue, setTouched } = fieldApi;
    const { value, error } = fieldState;

    const handleSelect = useCallback(
      (value: V) => {
        setValue(value);
        setTouched(true);

        if (onSelectOption) {
          onSelectOption(value as any);
        }
      },
      [setValue, setTouched, onSelectOption]
    );

    const handleClose = useCallback(() => {
      setTouched(true);

      if (onClose) {
        onClose();
      }
    }, [setTouched, onClose]);

    function getError() {
      if (propsError) propsError;

      if (error != null && translateError) {
        return getErrorTranslation(t, error as ValidatorMessage);
      }
      return error;
    }

    return render(
      <Component
        {...userProps}
        {...fieldState}
        {...fieldApi}
        ref={ref}
        error={getError()}
        selected={value}
        onSelectOption={handleSelect}
        onClose={handleClose}
      />
    );
  };
}

export const Select = asSelectField(SelectBlock);

export const ProfileSelect = asSelectField(ProfileSelectBlock);
